import { useMemo, useState } from "react";
import { MessageCircle, Phone } from "lucide-react";
import { AppShell } from "@/shared/layouts/AppShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { LoadingState } from "@/shared/ui/spinner.jsx";
import { cn } from "@/shared/lib/cn.js";
import { initials, formatDateTime } from "@/shared/lib/format.js";
import { LeadDrawer } from "@/features/leads/components/LeadDrawer.jsx";
import { CreateLeadDialog } from "@/features/leads/components/CreateLeadDialog.jsx";
import { useLeads, useUpdateLeadStatus } from "@/features/leads/use-leads.js";
import { STAGES, temperatureDot } from "@/features/leads/lead-constants.js";

/**
 * Pipeline visual (kanban).
 *
 * Usa exatamente o mesmo `LeadDrawer` da tela de Leads — no app antigo o
 * painel estava duplicado aqui, com as mesmas 230 linhas.
 */
export function CrmPage() {
  const { data: leads = [], isLoading } = useLeads();
  const mudarEtapa = useUpdateLeadStatus();

  const [arrastando, setArrastando] = useState(null);
  const [colunaAlvo, setColunaAlvo] = useState(null);
  const [leadAberto, setLeadAberto] = useState(null);

  /** Agrupa uma vez só, em vez de filtrar a lista dentro de cada coluna. */
  const porEtapa = useMemo(() => {
    const mapa = Object.fromEntries(STAGES.map((s) => [s.id, []]));
    for (const lead of leads) (mapa[lead.status] ??= []).push(lead);
    return mapa;
  }, [leads]);

  function soltar(status) {
    setColunaAlvo(null);
    if (!arrastando) return;
    const lead = leads.find((l) => l.id === arrastando);
    setArrastando(null);
    if (!lead || lead.status === status) return;
    mudarEtapa.mutate({ id: lead.id, status });
  }

  return (
    <AppShell
      title="CRM Comercial"
      subtitle="Pipeline visual · arraste cards entre etapas"
      actions={<CreateLeadDialog />}
    >
      {isLoading ? (
        <LoadingState message="Carregando pipeline..." />
      ) : (
        <div className="p-6">
          <div className="scrollbar-slim flex gap-4 overflow-x-auto pb-4">
            {STAGES.map((etapa) => {
              const itens = porEtapa[etapa.id] ?? [];
              const destacada = colunaAlvo === etapa.id;

              return (
                <div
                  key={etapa.id}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setColunaAlvo(etapa.id);
                  }}
                  onDragLeave={() => setColunaAlvo((atual) => (atual === etapa.id ? null : atual))}
                  onDrop={() => soltar(etapa.id)}
                  className="flex w-70 shrink-0 flex-col"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={cn("h-2 w-2 rounded-full", etapa.dot)} />
                      <h3 className="text-xs font-semibold uppercase tracking-wider">
                        {etapa.title}
                      </h3>
                    </div>
                    <Badge variant="secondary" className="h-5 px-1.5 text-[10px]">
                      {itens.length}
                    </Badge>
                  </div>

                  <div
                    className={cn(
                      "min-h-50 flex-1 space-y-2 rounded-xl p-2 transition-colors",
                      destacada ? "bg-primary/10 ring-2 ring-primary/30" : "bg-muted/30",
                    )}
                  >
                    {itens.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onDragStart={() => setArrastando(lead.id)}
                        onDragEnd={() => {
                          setArrastando(null);
                          setColunaAlvo(null);
                        }}
                        onOpen={() => setLeadAberto(lead)}
                      />
                    ))}

                    {itens.length === 0 && (
                      <p className="px-2 py-6 text-center text-[11px] text-muted-foreground">
                        Nenhum lead nesta etapa
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <LeadDrawer
        lead={leadAberto ? (leads.find((l) => l.id === leadAberto.id) ?? leadAberto) : null}
        onClose={() => setLeadAberto(null)}
      />
    </AppShell>
  );
}

function LeadCard({ lead, onDragStart, onDragEnd, onOpen }) {
  return (
    <Card
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      onKeyDown={(e) => e.key === "Enter" && onOpen()}
      tabIndex={0}
      role="button"
      aria-label={`Abrir lead ${lead.name}`}
      className="cursor-pointer p-3 transition-all hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{lead.name}</p>
          <p className="truncate text-[11px] text-muted-foreground">{lead.procedure || "—"}</p>
        </div>
        <span
          className={cn("h-2 w-2 shrink-0 rounded-full", temperatureDot(lead.temperature))}
          title={lead.temperature}
        />
      </div>

      <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
        {lead.phone && (
          <div className="flex items-center gap-1">
            <Phone className="h-3 w-3" />
            {lead.phone}
          </div>
        )}
        <div className="flex items-center gap-1">
          <MessageCircle className="h-3 w-3" />
          {formatDateTime(lead.lastInteractionAt ?? lead.createdAt)}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between border-t border-border/60 pt-2">
        <span className="truncate text-[10px] text-muted-foreground">{lead.origin || "—"}</span>
        <div
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[9px] font-semibold text-primary"
          title={lead.owner || "Sem responsável"}
        >
          {lead.owner ? initials(lead.owner) : "—"}
        </div>
      </div>
    </Card>
  );
}
